import React, { useState } from "react";
import { Flag, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { likeHeaders } from "@/lib/useLikes";
import { useHiddenAuthors } from "@/lib/useHidden";
import { trackEvent } from "@/lib/analytics";
import { storage } from "@/lib/storage";

const API = process.env.REACT_APP_BACKEND_URL;
const REASONS = ["spam", "offensive", "inappropriate", "copyright", "privacy", "other"];

/** Report a review/photo/video (Play UGC policy) + hide its author on this device. */
export default function ReportButton({ item, dark = false, size = "sm", onHidden }) {
  const { t } = useLang();
  const { hide } = useHiddenAuthors();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };
  const send = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/reviews/${item.id}/report`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json", ...likeHeaders() }, body: JSON.stringify({ reason, note }) });
      if (!r.ok) throw new Error("report");
      trackEvent("report_content", { reason }); storage.set("atlas.hasReported", "1");
      toast.success(t("report_sent")); setOpen(false);
    } catch { toast.error(t("report_error")); } finally { setBusy(false); }
  };
  const hideAuthor = () => { hide(item.author?.key); trackEvent("hide_author"); toast(t("hide_done").replace("{name}", item.author?.name || "")); setOpen(false); onHidden?.(); };
  const btn = size === "md" ? "w-9 h-9" : "w-7 h-7";
  return (
    <>
      <button onClick={(e) => { stop(e); setOpen(true); }} aria-label={t("report_btn")} title={t("report_btn")} className={`${btn} rounded-full inline-flex items-center justify-center transition-colors ${dark ? "bg-sand/10 hover:bg-sand/25 text-sand" : "text-muted-foreground hover:text-red-700 hover:bg-red-50"}`} data-testid={`report-btn-${item.id}`}>
        <Flag className={size === "md" ? "w-4 h-4" : "w-3.5 h-3.5"} />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm z-[90]" onClick={stop} onEscapeKeyDown={(e) => e.stopPropagation()} data-testid="report-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("report_title")}</DialogTitle><DialogDescription>{t("report_sub")}</DialogDescription></DialogHeader>
          <div className="grid gap-1.5" data-testid="report-reasons">
            {REASONS.map((r) => <button key={r} onClick={() => setReason(r)} className={`rounded-xl border px-3 py-2 text-sm text-start transition-colors ${reason === r ? "border-red-600 bg-red-50 text-red-800" : "border-stoneLine hover:border-ochre/60"}`} data-testid={`report-reason-${r}`}>{t(`report_${r}`)}</button>)}
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} rows={2} placeholder={t("report_note")} className="w-full rounded-xl border border-stoneLine bg-card px-3 py-2 text-sm" data-testid="report-note" />
          <button onClick={send} disabled={!reason || busy} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-red-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-red-700 disabled:opacity-40" data-testid="report-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />} {t("report_send")}</button>
          <button onClick={hideAuthor} className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-stoneLine px-4 py-2 text-sm hover:bg-sandDeep/50" data-testid="hide-author"><EyeOff className="w-4 h-4" /> {t("hide_author").replace("{name}", item.author?.name || "")}</button>
          <p className="text-[11px] text-muted-foreground">{t("report_hint")}</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
